import { Star, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { NpsQuestion } from '@/shared/types/nps';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { DatePicker } from '@/shared/ui/date-picker';
import { cn } from '@/shared/lib/cn';
import { getPublicSurveyDateLocale } from './i18n/date-locale';

export type AnswerValue = string | number | boolean | string[] | undefined;

function RangeInput({
  min,
  max,
  value,
  onChange,
  mobile,
  t,
}: {
  min: number;
  max: number;
  value: number | undefined;
  onChange: (v: number) => void;
  mobile?: boolean;
  t: TFunction;
}) {
  const items: number[] = [];
  for (let n = min; n <= max; n++) items.push(n);
  // Rede de segurança: se a escala configurada no admin for muito maior que o
  // 0-10 padrão do NPS, evita um grid de dezenas de botões tomando a tela —
  // vira uma faixa com rolagem horizontal em vez de estourar em várias linhas.
  const tooMany = items.length > 15;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          tooMany
            ? 'no-scrollbar flex items-center gap-1.5 overflow-x-auto pb-1'
            : 'flex flex-wrap items-center justify-center gap-1.5 sm:gap-2',
          mobile && !tooMany && 'gap-1',
        )}
      >
        {items.map((num) => (
          <Button
            key={num}
            type="button"
            size="icon"
            onClick={() => onChange(num)}
            className={cn(
              'rounded-lg font-bold text-white',
              mobile ? 'size-11 min-w-[2.75rem] text-base' : 'size-10',
              num >= 0 && num <= 6
                ? 'bg-red-500 hover:bg-red-600'
                : num === 7 || num === 8
                  ? 'bg-yellow-500 hover:bg-yellow-600'
                  : 'bg-green-500 hover:bg-green-600',
              value === num && 'ring-2 ring-foreground ring-offset-2 ring-offset-background',
            )}
          >
            {num}
          </Button>
        ))}
      </div>
      <p className="text-center text-sm font-medium">
        {t('questionField.selectedValue', { value: value ?? 0 })}
      </p>
    </div>
  );
}

function StarInput({
  max,
  value,
  onChange,
  mobile,
  t,
}: {
  max: number;
  value: number;
  onChange: (v: number) => void;
  mobile?: boolean;
  t: TFunction;
}) {
  return (
    <div className="flex justify-center gap-0.5 sm:gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i + 1)}
          aria-label={t('questionField.starsAria', { count: i + 1 })}
          className={cn('p-1.5 sm:p-1', mobile && 'p-2')}
        >
          <Star
            className={cn(
              'transition-colors',
              mobile ? 'size-10' : 'size-8',
              i < value ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40',
            )}
          />
        </button>
      ))}
    </div>
  );
}

export function PublicSurveyQuestionField({
  q,
  value,
  onChange,
  mobile = false,
  enterSubmits = false,
}: {
  q: NpsQuestion;
  value: AnswerValue;
  onChange: (v: AnswerValue) => void;
  mobile?: boolean;
  enterSubmits?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = getPublicSurveyDateLocale(i18n.language);

  switch (q.response_type) {
    case 'RANGE':
      return (
        <RangeInput
          min={q.response_metadata?.min ?? 0}
          max={q.response_metadata?.max ?? 10}
          value={value as number | undefined}
          onChange={onChange}
          mobile={mobile}
          t={t}
        />
      );
    case 'STAR':
      return (
        <StarInput
          max={q.response_metadata?.max ?? 5}
          value={(value as number) ?? 0}
          onChange={onChange}
          mobile={mobile}
          t={t}
        />
      );
    case 'BOOLEAN':
      return (
        <div className="flex justify-center gap-3">
          <button
            type="button"
            data-checked={value === true}
            onClick={() => onChange(true)}
            className={cn(
              'flex items-center justify-center rounded-xl border border-border transition-colors hover:bg-green-500 hover:text-white data-[checked=true]:bg-green-500 data-[checked=true]:text-white',
              mobile ? 'size-14' : 'size-12',
            )}
          >
            <ThumbsUp className={mobile ? 'size-6' : 'size-5'} />
          </button>
          <button
            type="button"
            data-checked={value === false}
            onClick={() => onChange(false)}
            className={cn(
              'flex items-center justify-center rounded-xl border border-border transition-colors hover:bg-red-500 hover:text-white data-[checked=true]:bg-red-500 data-[checked=true]:text-white',
              mobile ? 'size-14' : 'size-12',
            )}
          >
            <ThumbsDown className={mobile ? 'size-6' : 'size-5'} />
          </button>
        </div>
      );
    case 'TEXT':
      return q.response_metadata?.input_type === 'textarea' ? (
        <Textarea
          rows={4}
          placeholder={t('placeholders.textarea')}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(mobile && 'min-h-[120px] text-base')}
        />
      ) : (
        <Input
          placeholder={t('placeholders.text')}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={cn(mobile && 'h-12 text-base')}
          enterKeyHint="go"
          onKeyDown={
            enterSubmits
              ? (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
                  }
                }
              : undefined
          }
        />
      );
    case 'DATE':
      return (
        <DatePicker
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
          placeholder={t('placeholders.date')}
          locale={dateLocale}
        />
      );
    case 'SELECT':
      return (
        <div className="space-y-2">
          {(q.response_metadata?.options ?? []).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg border px-4 text-left text-sm transition-all',
                mobile ? 'min-h-12 py-3 text-base' : 'py-2.5',
                value === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
              )}
            >
              <span
                className={cn(
                  'size-4 rounded-full border',
                  value === opt.value ? 'border-[5px] border-primary' : 'border-muted-foreground/40',
                )}
              />
              {opt.label}
            </button>
          ))}
        </div>
      );
    case 'CHECKBOX': {
      const arr = (value as string[]) ?? [];
      return (
        <div className="space-y-2">
          {(q.response_metadata?.options ?? []).map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border border-border px-4 text-sm hover:border-primary/40',
                  mobile ? 'min-h-12 py-3 text-base' : 'py-2.5',
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) =>
                    onChange(c ? [...arr, opt.value] : arr.filter((v) => v !== opt.value))
                  }
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    default:
      return null;
  }
}

export function isAnswerEmpty(value: AnswerValue): boolean {
  if (value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function cpfDigits(value: string): string {
  return value.replace(/\D/g, '');
}
